import {useEffect, useMemo} from 'preact/hooks';

import {
  defineShopPayBannerElement,
  defineShopPayInstallmentsBannerElement,
} from '~/__deprecated__/components/paymentTerms';
import {isShopifyMetadata} from '~/features/PaymentTerms/utils/metadataValidation';
import {RootProvider} from '~/foundation/RootProvider/RootProvider';
import register from '~/utils/register';

import type {
  ShopPayBannerWebComponentProps,
  ShopPayInstallmentsBannerWebComponentProps,
  ShopifyPaymentTermsProps,
  ShopifyPaymentTermsWebComponentProps,
} from './types';

const ShopifyPaymentTermsInternal = ({
  shopifyMeta,
  variantId,
}: ShopifyPaymentTermsProps) => {
  useEffect(() => {
    defineShopPayBannerElement();
    defineShopPayInstallmentsBannerElement();
  }, []);

  const isValidMetadata = useMemo(
    () => shopifyMeta && isShopifyMetadata(JSON.parse(shopifyMeta)),
    [shopifyMeta],
  );

  return isValidMetadata ? (
    <shop-pay-installments-banner
      shopify-meta={shopifyMeta}
      variant-id={variantId}
    />
  ) : (
    <shop-pay-banner shopify-meta={shopifyMeta} variant-id={variantId} />
  );
};

// Register the component as a custom element
register<ShopifyPaymentTermsProps>(
  ({element, ...props}) => (
    <RootProvider element={element} featureName="ShopifyPaymentTerms">
      <ShopifyPaymentTermsInternal {...props} />
    </RootProvider>
  ),
  {
    name: 'shopify-payment-terms',
    props: {
      variantId: 'string',
      shopifyMeta: 'string',
    },
    shadow: 'open',
  },
);

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace preact.JSX {
    interface IntrinsicElements {
      ['shopify-payment-terms']: ShopifyPaymentTermsWebComponentProps;
      ['shop-pay-installments-banner']: ShopPayInstallmentsBannerWebComponentProps;
      ['shop-pay-banner']: ShopPayBannerWebComponentProps;
    }
  }
}

export function ShopifyPaymentTerms(
  props: ShopifyPaymentTermsWebComponentProps,
) {
  return <shopify-payment-terms {...props} />;
}
